import { OrderDesignComment } from "@jasper/client-models";
import {
  Avatar,
  Box,
  Card,
  Divider,
  Flex,
  Group,
  Image,
  Indicator,
  Stack,
  Tabs,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconFileSettings,
  IconFileTypePdf,
  IconPhoto,
  IconProps,
} from "@tabler/icons-react";
import { useState } from "react";
import { OrderDesignCommentTranslationModal } from "./OrderDesignCommentTranslationModal";
import { useTranslateOrderDesignComment } from "@jasper/use-cases";

interface Props {
  comments: OrderDesignComment[];
  tab?: string;
}

const isImageAttachment = (attachment: { filename: string; url: string }) => {
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];
  const extension = attachment.filename.split(".").pop()?.toLowerCase();
  return extension ? imageExtensions.includes(extension) : false;
};

const AttachmentIcon = ({
  attachment,
  ...props
}: IconProps & { attachment: { filename: string; url: string } }) => {
  if (isImageAttachment(attachment)) {
    return <IconPhoto {...props} />;
  }

  const extension = attachment.filename.split(".").pop()?.toLowerCase();

  if (extension === "pdf") {
    return <IconFileTypePdf {...props} />;
  }

  return <IconFileSettings {...props} />;
};

export const OrderDesignCommentTab = ({
  comments,
  tab = "comments",
}: Props) => {
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false);
  const [commentToTranslate, setCommentToTranslate] =
    useState<OrderDesignComment | null>(null);

  const { translateComment, isTranslatingComment } =
    useTranslateOrderDesignComment();

  const openTranslationModal = (comment: OrderDesignComment) => {
    setCommentToTranslate(comment);
    setIsTranslationModalOpen(true);
  };

  const closeTranslationModal = () => {
    setCommentToTranslate(null);
    setIsTranslationModalOpen(false);
  };

  return (
    <>
      <OrderDesignCommentTranslationModal
        comment={commentToTranslate ?? undefined}
        isOpen={isTranslationModalOpen}
        onClose={closeTranslationModal}
        isLoading={isTranslatingComment}
        onSave={async ({ translation }) => {
          if (!commentToTranslate) {
            return;
          }

          await translateComment({
            commentId: commentToTranslate.id,
            translation,
          });
          closeTranslationModal();
        }}
      />
      <Tabs.Panel value={tab}>
        <Group py="xs">
          {comments.length === 0 && (
            <Flex w="100%" justify="center" align="center" p="md">
              <Text>No comments yet</Text>
            </Flex>
          )}
          {comments.map(comment => (
            <Indicator
              w="100%"
              key={comment.id}
              label={
                <Tooltip label="Translate">
                  <UnstyledButton onClick={() => openTranslationModal(comment)}>
                    <Text size="xs">Missing translation</Text>
                  </UnstyledButton>
                </Tooltip>
              }
              position="top-center"
              size="xl"
              color="red"
              disabled={!!comment.translatedComment}
            >
              <Group
                key={comment.id}
                align="flex-start"
                bg={comment.translatedComment ? undefined : "red.0"}
                pt={comment.translatedComment ? "xs" : "md"}
                px="md"
              >
                <Avatar>
                  {comment.author.firstName[0]}
                  {comment.author.lastName[0]}
                </Avatar>
                <Stack gap="0">
                  <Group gap="xs">
                    <Text size="xs" c="gray">
                      {comment.author.firstName} {comment.author.lastName}
                    </Text>
                    <Divider orientation="vertical" />
                    <Text size="xs" c="gray">
                      {new Date(comment.createdAt).toLocaleString(undefined, {
                        dateStyle: "long",
                        timeStyle: "medium",
                      })}
                    </Text>
                  </Group>
                  <Stack gap={0}>
                    <Text>{comment.comment}</Text>
                    {comment.translatedComment && (
                      <Text c="gray" size="xs">
                        {comment.translatedComment}
                      </Text>
                    )}
                    {comment.attachments.length > 0 && (
                      <Group>
                        {comment.attachments.map((attachment, index) => (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Card withBorder h="80px" w="80px" p={0}>
                              <Tooltip label={attachment.filename}>
                                <Stack
                                  justify="space-between"
                                  h="100%"
                                  w="100%"
                                >
                                  <Box h="100px">
                                    {isImageAttachment(attachment) ? (
                                      <Image
                                        src={attachment.url}
                                        alt={attachment.filename}
                                        fit="fill"
                                      />
                                    ) : (
                                      <Flex
                                        w="100%"
                                        h="100%"
                                        justify="center"
                                        align="center"
                                      >
                                        <AttachmentIcon
                                          attachment={attachment}
                                          size="xl"
                                          color="gray"
                                        />
                                      </Flex>
                                    )}
                                  </Box>
                                </Stack>
                              </Tooltip>
                            </Card>
                          </a>
                        ))}
                      </Group>
                    )}
                  </Stack>
                </Stack>
              </Group>
            </Indicator>
          ))}
        </Group>
      </Tabs.Panel>
    </>
  );
};
